import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, iif, Subject, timer } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { EcommerceAnalyticsService } from '../../../analytics/ecommerce-analytics/ecommerce-analytics.service';
import { SECTIONS } from '../../../models/Constants';
import { UtilitiesService } from '../../../services/utilities/utilities.service';
import {
  DEFAULT_FAVS_CONTROL,
  ESTATUS_COMPLETADO,
  ESTATUS_EN_PROGRESO,
  ESTATUS_EXPIRADO,
  FavsControlDias,
  TIME_TO_CHANGE_SLIDE
} from './../../../models/recompepsi.interface';

@Component({
  selector: 'getm-favs-control',
  templateUrl: './favs-control.component.html',
  styleUrls: ['./favs-control.component.scss'],
})
export class FavsControlComponent implements OnInit, OnDestroy {

  @Input() set favsControl(val: FavsControlDias) {
    this.favsControlFormat = {
      ...val,
      diasDiferenciaVigencia: this.calcularDiferenciaDias(val.fechaFin),
      diasDiferenciaProximo: this.calcularDiferenciaDias(val.fechaProximoInicio, true)
    };
    this.diasVigenciaAnalytics = this.formatearDiasVigenciaAnalytics(val.fechaFin);
  }

  @Input() section: string = SECTIONS.TIENDA;

  public activeSlide: number = 0;
  public favsControlFormat: FavsControlDias = DEFAULT_FAVS_CONTROL;
  public diasVigenciaAnalytics: string = '';
  public readonly estatusProgreso = ESTATUS_EN_PROGRESO;
  public readonly estatusCompletado = ESTATUS_COMPLETADO;
  public readonly estatusExpirado = ESTATUS_EXPIRADO;

  private finishSubscriptions: Subject<boolean> = new Subject();

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private ecommerceAnalyticsS: EcommerceAnalyticsService,
    private utilitiesS: UtilitiesService
  ) { }

  ngOnInit() {
    iif(() => this.favsControlFormat.estatus === this.estatusCompletado,
      timer(TIME_TO_CHANGE_SLIDE, TIME_TO_CHANGE_SLIDE).pipe(map((tick) => (tick + 1) % 2)),
      EMPTY
    )
      .pipe(
        takeUntil(this.finishSubscriptions)
      )
      .subscribe(this.callBackStartAutoPlay);

    this.trackAvanceFavoritos();
    this.trackFinalizadoFavoritos();
  }

  ngOnDestroy() {
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
  }

  /**
   * Dispara el evento analítico 'avance_favoritos' ÚNICAMENTE cuando el estatus es ESTATUS_EN_PROGRESO (2).
   */
  public trackAvanceFavoritos(): void {
    if (this.favsControlFormat.estatus === this.estatusProgreso) {
      this.ecommerceAnalyticsS.sendAvanceFavoritos({
        meta_propuesta: this.favsControlFormat.skuObjetivos || 0,
        avance: this.favsControlFormat.skuLiquidados || 0,
        termina_en: this.diasVigenciaAnalytics,
        puntos_a_ganar: this.favsControlFormat.puntosPremio || 0,
        section: this.section
      });
    }
  }

  /**
   * Dispara el evento analítico 'finalizado_favoritos' cuando el estatus es
   * ESTATUS_COMPLETADO (3) o ESTATUS_EXPIRADO (4).
   * El servicio de analíticas se encarga de asegurar que solo se emita 1 sola vez por ciclo.
   */
  public trackFinalizadoFavoritos(): void {
    const estatus = this.favsControlFormat.estatus;
    if (estatus === this.estatusCompletado || estatus === this.estatusExpirado) {
      const estadoStr = estatus === this.estatusCompletado ? 'finalizado meta alcanzada' : 'finalizado no logrado';
      const diasProximo = this.utilitiesS.obtenerDiferenciaEnDias(this.favsControlFormat.fechaProximoInicio);

      this.ecommerceAnalyticsS.sendFinalizadoFavoritos({
        meta_propuesta: this.favsControlFormat.skuObjetivos || 0,
        avance: this.favsControlFormat.skuLiquidados || 0,
        termina_en: this.utilitiesS.formatearFechaTermino(this.favsControlFormat.fechaFin),
        estado: estadoStr,
        nuevo_reto_en: `${diasProximo !== null ? diasProximo : 0}d`,
        puntos_recibidos: this.favsControlFormat.puntosPremio || 0,
        section: this.section,
        idPilar: this.favsControlFormat.idPilar || 4,
        fechaInicio: this.favsControlFormat.fechaInicio || '',
        fechaFin: this.favsControlFormat.fechaFin || '',
        estatus: this.favsControlFormat.estatus
      });
    }
  }

  /**
   * Formatea la vigencia en texto limpio para el parámetro termina_en de analíticas.
   */
  public formatearDiasVigenciaAnalytics(fechaObjetivo: string): string {
    const diffDias = this.utilitiesS.obtenerDiferenciaEnDias(fechaObjetivo);
    if (diffDias === null) return '';
    if (diffDias < 1) return 'hoy';
    return diffDias === 1 ? '1d' : `${diffDias}d`;
  }

  /**
   * Formatea la etiqueta HTML para la UI.
   */
  private calcularDiferenciaDias(fechaObjetivo: string, isProximo: boolean = false): string {
    const diffDias = this.utilitiesS.obtenerDiferenciaEnDias(fechaObjetivo);
    if (diffDias === null) return '';

    const copy = this.translate.instant('Labels.' + (isProximo ? diffDias < 1 ? 'new-challenge' : 'new-challenge-in' : diffDias < 1 ? 'ends' : 'ends-in'));
    const nameDate = this.translate.instant('Labels.' + (isProximo ? 'tomorrow' : 'today'));
    const dayCopy = diffDias < 1 ? `<getm-error-default style="font-weight: 700;">${nameDate}</getm-error-default>` : `<getm-primary-default style="font-weight: 700;">${diffDias}d</getm-primary-default>`;
    return `${copy} ${dayCopy}`;
  }

  private callBackStartAutoPlay = (slideIndex: number) => {
    this.activeSlide = slideIndex;
    this.cdr.markForCheck();
  };
}
